// Note: Sentry import is commented out to avoid TypeScript errors
// import * as Sentry from 'sentry-expo';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

// Keys whose values must never appear in logs. Matched case-insensitively
// against object keys (exact or partial). Extend as new sensitive fields
// are introduced upstream.
const SENSITIVE_KEY_PATTERNS = [
  /^password$/i,
  /^current[_-]?password$/i,
  /^new[_-]?password$/i,
  /^confirm[_-]?password$/i,
  /^token$/i,
  /^access[_-]?token$/i,
  /^refresh[_-]?token$/i,
  /^verification[_-]?token$/i,
  /^auth[_-]?token$/i,
  /^secret$/i,
  /^api[_-]?key$/i,
  /^otp$/i,
  /^pin$/i,
  /^authorization$/i,
  /^cookie$/i,
  /^set-cookie$/i,
];

const REDACTED = '[REDACTED]';
const MAX_DEPTH = 6;

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERNS.some((re) => re.test(key));
}

/**
 * Redact x-www-form-urlencoded payloads like:
 *   username=foo%40bar&password=hunter2&grant_type=password
 */
function redactFormUrlEncoded(body: string): string {
  if (!body.includes('=') || !body.includes('&') && !isSensitiveKey(body.split('=')[0] ?? '')) {
    // Single-pair form bodies or non-form strings: check the first key
    const eq = body.indexOf('=');
    if (eq > -1 && isSensitiveKey(body.slice(0, eq))) {
      return `${body.slice(0, eq)}=${REDACTED}`;
    }
  }
  return body
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq < 0) return pair;
      const key = pair.slice(0, eq);
      if (isSensitiveKey(decodeURIComponent(key))) {
        return `${key}=${REDACTED}`;
      }
      return pair;
    })
    .join('&');
}

/**
 * Redact a JSON body string like '{"password":"hunter2","email":"..."}' by
 * parsing, scrubbing, and re-stringifying. Falls back to the original if
 * parsing fails.
 */
function redactJsonString(body: string): string {
  try {
    const parsed = JSON.parse(body);
    return JSON.stringify(scrub(parsed, 0));
  } catch {
    return body;
  }
}

function scrubString(key: string | undefined, value: string): string {
  if (key && isSensitiveKey(key)) return REDACTED;
  // Auto-detect a "Bearer <token>" header value even if we missed the key.
  if (/^Bearer\s+\S+/i.test(value)) return 'Bearer [REDACTED]';
  // Axios config.data is often a raw form-encoded or JSON string — redact
  // sensitive fields inside it.
  if (key === 'data' || key === 'body') {
    if (value.trim().startsWith('{') && value.trim().endsWith('}')) {
      return redactJsonString(value);
    }
    if (value.includes('=')) {
      return redactFormUrlEncoded(value);
    }
  }
  return value;
}

function scrub(value: unknown, depth: number, key?: string): unknown {
  if (depth > MAX_DEPTH) return '[MAX_DEPTH]';
  if (value === null || value === undefined) return value;
  if (key && isSensitiveKey(key)) return REDACTED;

  if (typeof value === 'string') return scrubString(key, value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;

  if (Array.isArray(value)) {
    return value.map((v) => scrub(v, depth + 1));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = scrub(v, depth + 1, k);
    }
    return out;
  }
  return value;
}

/**
 * Exported so other log-producers (breadcrumbs, analytics) can reuse the
 * same redaction logic before emitting events.
 */
export function sanitizeForLogging<T>(value: T): T {
  return scrub(value, 0) as T;
}

class Logger {
  private currentLevel: LogLevel;

  constructor() {
    this.currentLevel = __DEV__ ? LogLevel.DEBUG : LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  private formatMessage(level: string, message: string, extra?: any): string {
    const timestamp = new Date().toISOString();
    const safe = extra !== undefined ? sanitizeForLogging(extra) : undefined;
    const extraStr = safe !== undefined ? ` | ${JSON.stringify(safe)}` : '';
    return `[${timestamp}] ${level}: ${message}${extraStr}`;
  }

  debug(message: string, extra?: any) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage('DEBUG', message, extra));
    }
  }

  info(message: string, extra?: any) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage('INFO', message, extra));
    }
  }

  warn(message: string, extra?: any) {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, extra));
    }

    if (!__DEV__) {
      // TODO: Enable Sentry integration
      // Sentry.captureMessage(message, 'warning');
      // if (extra) Sentry.setExtra('context', sanitizeForLogging(extra));
      console.warn('[SENTRY]', message, extra !== undefined ? sanitizeForLogging(extra) : undefined);
    }
  }

  error(message: string, error?: any) {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', message, error));
    }

    if (!__DEV__) {
      // TODO: Enable Sentry integration
      // if (error instanceof Error) {
      //   Sentry.captureException(error);
      // } else {
      //   Sentry.captureMessage(message, 'error');
      // }
      // if (error && !(error instanceof Error)) {
      //   Sentry.setExtra('errorContext', sanitizeForLogging(error));
      // }
      console.error('[SENTRY]', message, error !== undefined ? sanitizeForLogging(error) : undefined);
    }
  }

  setLevel(level: LogLevel) {
    this.currentLevel = level;
  }
}

export const logger = new Logger();
export default logger;

// ─────────────────────────────────────────────────────────────────────
// FE-251 — Performance instrumentation layer.
//
// Built on top of the existing logger so all the redaction + log-level
// gating already in place applies. Three primitives:
//
//   - `perf.time(label)` / `perf.timeEnd(label)` — bracket a sync block
//     OR an awaited promise; logs the elapsed ms with category=PERF.
//   - `perf.span(label, fn)` — convenience: runs `fn` (sync or async),
//     measures, and logs in one call. Returns whatever `fn` returns.
//   - `perf.mark(label, extra?)` — discrete log line for a single
//     point-in-time event (mount, dispatch fired, render flushed, …).
//
// Every entry carries an auto-incremented sequence number so logs from
// rapid bursts can be ordered when timestamps tie. A correlation id
// can be attached via `perf.withCorrelation(id, fn)` to thread one
// request across multiple async hops (e.g. user-tap → effect → thunk
// → axios → reducer → re-render).
//
// In production all perf logs are silenced via the LogLevel gate
// (LogLevel.INFO is the prod default; perf logs at LogLevel.DEBUG).
// You can flip them on with `logger.setLevel(LogLevel.DEBUG)` from
// the browser console for a session of investigation.
// ─────────────────────────────────────────────────────────────────────

let _perfSeq = 0;
const _perfMarks = new Map<string, { startedAt: number; correlationId?: string }>();
let _correlationStack: string[] = [];

function _now(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function _currentCorrelation(): string | undefined {
  return _correlationStack[_correlationStack.length - 1];
}

function _formatPerfLine(category: string, label: string, durationMs?: number, extra?: any) {
  const seq = ++_perfSeq;
  const cid = _currentCorrelation();
  const parts: string[] = [];
  parts.push(`[PERF#${seq}]`);
  if (cid) parts.push(`(cid=${cid})`);
  parts.push(`[${category}]`);
  parts.push(label);
  if (durationMs !== undefined) {
    parts.push(`(${durationMs.toFixed(1)}ms)`);
  }
  return { line: parts.join(' '), extra: extra !== undefined ? sanitizeForLogging(extra) : undefined };
}

export const perf = {
  /** Start a timer. Pair with `timeEnd(label)`. Same label semantics as
   *  console.time but works in RN + browsers + jest. */
  time(label: string, extra?: any) {
    _perfMarks.set(label, {
      startedAt: _now(),
      correlationId: _currentCorrelation(),
    });
    const { line, extra: e } = _formatPerfLine('start', label, undefined, extra);
    if (e !== undefined) {
      logger.debug(line, e);
    } else {
      logger.debug(line);
    }
  },
  /** Stop a previously-started timer and log the elapsed ms. Safe to
   *  call with an unknown label (logs `[no-start]` instead of crashing). */
  timeEnd(label: string, extra?: any): number {
    const mark = _perfMarks.get(label);
    if (!mark) {
      logger.debug(`[PERF] [no-start] ${label}`);
      return 0;
    }
    const elapsed = _now() - mark.startedAt;
    _perfMarks.delete(label);
    const { line, extra: e } = _formatPerfLine('end', label, elapsed, extra);
    if (e !== undefined) {
      logger.debug(line, e);
    } else {
      logger.debug(line);
    }
    return elapsed;
  },
  /** Bracket a function call. Returns the function's return value. */
  async span<T>(label: string, fn: () => T | Promise<T>, extra?: any): Promise<T> {
    this.time(label, extra);
    try {
      const result = await fn();
      this.timeEnd(label);
      return result;
    } catch (err: any) {
      this.timeEnd(label, { error: err?.message ?? String(err) });
      throw err;
    }
  },
  /** Single point-in-time event. Useful for mount/unmount/effect-fired. */
  mark(label: string, extra?: any) {
    const { line, extra: e } = _formatPerfLine('mark', label, undefined, extra);
    if (e !== undefined) {
      logger.debug(line, e);
    } else {
      logger.debug(line);
    }
  },
  /** Thread a correlation id across nested async hops. Inside fn,
   *  every perf log is tagged with `cid=<id>` so a single user action
   *  can be followed end-to-end. Pure stack — pushes on entry, pops
   *  on exit (even if fn throws). */
  async withCorrelation<T>(id: string, fn: () => T | Promise<T>): Promise<T> {
    _correlationStack.push(id);
    try {
      return await fn();
    } finally {
      _correlationStack.pop();
    }
  },
  /** Drop any unfinished timers — useful in tests or on unmount when
   *  we want to forget pending marks without logging a confusing end. */
  reset() {
    _perfMarks.clear();
    _correlationStack = [];
  },
};
